import { Component, OnInit, OnDestroy, inject, NgZone, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PhotoboothService } from '../services/photobooth.service';
import { AccessService, PageAccessScope } from '../services/access.service';
import { BrandService } from '../services/brand.service';
import { CompanyService } from '../services/company.service';
import { StoreService } from '../services/store.service';
import { Brand } from '../models/brand.model';
import { Auth } from '@angular/fire/auth';
import { firstValueFrom, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { SessionDetailsModalComponent } from './session-details-modal/session-details-modal.component';
import * as XLSX from 'xlsx';

export interface FilteredRow {
  deviceId: string;
  storeId: string;
  CardTotal: number;
  CashTotal: number;
  CouponTotal: number;
  DailyAnimefiedGenerated: number;
  DailyAnimefiedUsed: number;
  DailyOverpaidAmount: number;
  DailyRevenue: number;
  DailySessions: number;
  Country?: string;
  PrintsLeft?: number;
  StoreTotal?: number;
  deviceOnline?: boolean;
  cameraConnected?: boolean;
  cardMachineConnected?: boolean;
  billAcceptorConnected?: boolean;
  isDailyOverridden?: boolean;
  isMonthlyOverridden?: boolean;
  overrideReason?: string;
  updatedBy?: string;
  updatedAt?: any;
  originalValues?: any;
  overrideHistory?: any[];
}
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-daily-report',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './daily-report.component.html',
  styleUrls: ['./daily-report.component.css'],
})
export class DailyReportComponent implements OnInit, OnDestroy {
  private photoboothService = inject(PhotoboothService);
  private accessService = inject(AccessService);
  private brandService = inject(BrandService);
  private companyService = inject(CompanyService);
  private storeService = inject(StoreService);
  private auth = inject(Auth);
  private zone = inject(NgZone);
  private dialog = inject(MatDialog);

  private brandSub?: Subscription;
  selectedBrand: Brand | null = null;
  unassignedDevicesCount = 0;
  isSuperAdmin = false;

  get isKonoboxBrand(): boolean {
    const brandName = (this.selectedBrand?.name || '').toLowerCase();
    const brandId = (this.selectedBrand?.id || '').toLowerCase();
    return brandName.includes('konobox') || brandId === 'brand_konobox' || brandId === 'konobox';
  }

  userScope: PageAccessScope | null = null;
  reportData: any[] = [];
  filteredData: FilteredRow[] = [];
  reportCache = new Map<string, any[]>();

  isLoading = false;
  
  selectedDate: Date = new Date();
  isDateRange = false;
  endDate: Date = new Date();
  
  selectedRow: FilteredRow | null = null;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  
  activeTooltipDeviceId: string | null = null;

  toggleTooltip(deviceId: string, event: MouseEvent) {
    event.stopPropagation();
    this.activeTooltipDeviceId = this.activeTooltipDeviceId === deviceId ? null : deviceId;
  }

  selectRow(row: FilteredRow) {
    this.selectedRow = this.selectedRow === row ? null : row;
  }
  
  sortBy(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  applySort() {
    if (this.sortColumn) {
      this.allRows.sort((a, b) => {
        let valA = (a as any)[this.sortColumn] ?? '';
        let valB = (b as any)[this.sortColumn] ?? '';
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
        
        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    this.applyFilter();
  }

  toggleDateRange(event?: Event) {
    if (event) {
      const target = event.target as HTMLInputElement;
      this.isDateRange = target.checked;
    } else {
      this.isDateRange = !this.isDateRange;
    }

    if (this.isDateRange) {
      this.endDate = new Date(this.selectedDate.getTime());
    }
    this.fetchReport();
  }

  onEndDateChange() {
    if (this.isDateRange && this.endDate < this.selectedDate) {
      this.selectedDate = new Date(this.endDate.getTime());
    }
    this.fetchReport();
  }

  exportToExcel() {
    if (!this.filteredData || this.filteredData.length === 0) {
      alert('No data available to export.');
      return;
    }

    const isKono = this.isKonoboxBrand;
    const exportRows = this.filteredData.map(row => {
      const baseRow: any = {
        'StoreID': row.storeId || '',
        'Country': row.Country || '',
        'Machine ID': row.deviceId || '',
        'Store Total': row.StoreTotal || 0,
        'Revenue': row.DailyRevenue || 0,
        'Card Total': row.CardTotal || 0,
        'Cash Total': row.CashTotal || 0,
        'Coupon Total': row.CouponTotal || 0,
        'Overpaid Amount': row.DailyOverpaidAmount || 0,
        'Sessions': row.DailySessions || 0
      };

      if (!isKono) {
        baseRow['Prints Left'] = row.PrintsLeft || 0;
        baseRow['Animefied Generated'] = row.DailyAnimefiedGenerated || 0;
        baseRow['Animefied Used'] = row.DailyAnimefiedUsed || 0;
      }

      return baseRow;
    });

    let worksheet: XLSX.WorkSheet;
    const sheetName = this.isDateRange ? 'Range Report' : 'Daily Report';

    if (this.isDateRange) {
      const startFormatted = this.selectedDate.toLocaleDateString('en-US');
      const endFormatted = this.endDate.toLocaleDateString('en-US');
      const rangeTitle = `Date Range: ${startFormatted} - ${endFormatted}`;
      
      // Cell A1 contains the Date Range header, table starts at A2
      worksheet = XLSX.utils.aoa_to_sheet([[rangeTitle]]);
      XLSX.utils.sheet_add_json(worksheet, exportRows, { origin: 'A2' });
    } else {
      worksheet = XLSX.utils.json_to_sheet(exportRows);
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const year = this.selectedDate ? this.selectedDate.getFullYear() : '';
    const month = this.selectedDate ? String(this.selectedDate.getMonth() + 1).padStart(2, '0') : '';
    const day = this.selectedDate ? String(this.selectedDate.getDate()).padStart(2, '0') : '';
    const dateStr = year && month && day ? `${year}-${month}-${day}` : 'report';

    const brandTag = this.selectedBrand?.name ? `_${this.selectedBrand.name.replace(/\s+/g, '_')}` : '';

    let fileName = `Daily_Report${brandTag}_${dateStr}.xlsx`;
    if (this.isDateRange) {
      const endY = this.endDate ? this.endDate.getFullYear() : '';
      const endM = this.endDate ? String(this.endDate.getMonth() + 1).padStart(2, '0') : '';
      const endD = this.endDate ? String(this.endDate.getDate()).padStart(2, '0') : '';
      const endDateStr = endY && endM && endD ? `${endY}-${endM}-${endD}` : '';
      fileName = `Range_Report${brandTag}_${dateStr}_to_${endDateStr}.xlsx`;
    }

    XLSX.writeFile(workbook, fileName);
  }
  
  openSessionDetails(row: FilteredRow) {
    const month = this.selectedDate.getMonth() + 1;
    const year = this.selectedDate.getFullYear();
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(this.selectedDate.getDate()).padStart(2, '0')}`;
    const formattedDate = this.selectedDate.toLocaleDateString();

    const endMonth = this.endDate.getMonth() + 1;
    const endYear = this.endDate.getFullYear();
    const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(this.endDate.getDate()).padStart(2, '0')}`;
    const formattedEndDate = this.endDate.toLocaleDateString();

    this.dialog.open(SessionDetailsModalComponent, {
      data: {
        deviceId: row.deviceId,
        storeId: row.storeId,
        date: dateStr,
        endDate: this.isDateRange ? endDateStr : dateStr,
        formattedDate: this.isDateRange ? `${formattedDate} - ${formattedEndDate}` : formattedDate,
        isKonoboxBrand: this.isKonoboxBrand
      },
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container'
    });
  }

  // --- Override Modal State & Methods ---
  isOverrideModalOpen = false;
  overrideTargetRow: FilteredRow | null = null;
  overrideCardTotal: number = 0;
  overrideCashTotal: number = 0;
  overrideCouponTotal: number = 0;
  overrideOverpaidAmount: number = 0;
  overrideSessions: number = 0;
  overrideReasonInput: string = '';
  isSavingOverride = false;

  // --- Override Details / Audit Modal ---
  isOverrideDetailsModalOpen = false;
  overrideDetailsRow: FilteredRow | null = null;

  // Calendar highlighted overridden dates
  overriddenDatesThisMonth: Set<string> = new Set<string>();

  dateClass = (cellDate: Date, view: string): string => {
    if (view === 'month') {
      const yyyy = cellDate.getFullYear();
      const mm = String(cellDate.getMonth() + 1).padStart(2, '0');
      const dd = String(cellDate.getDate()).padStart(2, '0');
      const dateStr = `${yyyy}-${mm}-${dd}`;
      if (this.overriddenDatesThisMonth.has(dateStr)) {
        return 'overridden-calendar-day';
      }
    }
    return '';
  };

  openOverrideModal(row: FilteredRow, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.overrideTargetRow = row;
    this.overrideCardTotal = row.CardTotal || 0;
    this.overrideCashTotal = row.CashTotal || 0;
    this.overrideCouponTotal = row.CouponTotal || 0;
    this.overrideOverpaidAmount = row.DailyOverpaidAmount || 0;
    this.overrideSessions = row.DailySessions || 0;
    this.overrideReasonInput = '';
    this.isOverrideModalOpen = true;
  }

  closeOverrideModal() {
    this.isOverrideModalOpen = false;
    this.overrideTargetRow = null;
    this.overrideReasonInput = '';
  }

  openOverrideDetailsModal(row: FilteredRow, event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    if (!row.isDailyOverridden) return;
    this.overrideDetailsRow = row;
    this.isOverrideDetailsModalOpen = true;
  }

  closeOverrideDetailsModal() {
    this.isOverrideDetailsModalOpen = false;
    this.overrideDetailsRow = null;
  }

  async saveOverrideNumbers() {
    if (!this.overrideTargetRow || this.isSavingOverride) return;

    if (!this.overrideReasonInput || !this.overrideReasonInput.trim()) {
      alert('Override reason is mandatory. Please provide a reason for modifying these numbers.');
      return;
    }

    this.isSavingOverride = true;
    const row = this.overrideTargetRow;

    const month = this.selectedDate.getMonth() + 1;
    const year = this.selectedDate.getFullYear();
    const period = `${year}-${String(month).padStart(2, '0')}`;
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(this.selectedDate.getDate()).padStart(2, '0')}`;

    const user = this.auth.currentUser;
    const updatedBy = user?.email || user?.displayName || user?.uid || 'Admin User';

    try {
      await this.photoboothService.overrideDailyAndMonthlyRevenue(
        row.deviceId,
        period,
        dateStr,
        Number(this.overrideCardTotal) || 0,
        Number(this.overrideCashTotal) || 0,
        {
          CouponTotal: Number(this.overrideCouponTotal) || 0,
          DailyOverpaidAmount: Number(this.overrideOverpaidAmount) || 0,
          DailySessions: Number(this.overrideSessions) || 0,
          DailyAnimefiedGenerated: row.DailyAnimefiedGenerated || 0,
          DailyAnimefiedUsed: row.DailyAnimefiedUsed || 0
        },
        this.overrideReasonInput.trim(),
        updatedBy
      );

      this.closeOverrideModal();
      await this.fetchReport(true);
    } catch (error) {
      console.error('Failed to override revenue numbers:', error);
      alert('Failed to override revenue numbers in database. Please try again.');
    } finally {
      this.isSavingOverride = false;
    }
  }

  // Track loaded period to minimize API calls
  private loadedMonth: number | null = null;
  private loadedYear: number | null = null;

  monthlyStats = {
    cash: 0,
    credit: 0,
    coupon: 0,
    overCharge: 0,
    grandTotal: 0
  };

  dailyStats = {
    cash: 0,
    credit: 0,
    coupon: 0,
    overCharge: 0,
    grandTotal: 0
  };

  // Dropdown States
  showStoreFilterDropdown = false;
  showDeviceFilterDropdown = false;
  showTestMachines = false;
  allDailyDataRaw: any[] = [];
  
  // Stores Filter
  allUniqueStores: string[] = [];
  selectedStores = new Set<string>();
  storeFilterSearchText = '';
  brandStoreLookupFailed = false;
  
  // Devices Filter
  allUniqueDevices: string[] = [];
  selectedDevices = new Set<string>();
  deviceFilterSearchText = '';

  get isAllStoresSelected(): boolean {
    return this.selectedStores.size === this.allUniqueStores.length && this.allUniqueStores.length > 0;
  }

  get isAllDevicesSelected(): boolean {
    return this.selectedDevices.size === this.allUniqueDevices.length && this.allUniqueDevices.length > 0;
  }

  toggleStoreFilter() {
    this.showStoreFilterDropdown = !this.showStoreFilterDropdown;
    this.showDeviceFilterDropdown = false;
  }

  toggleDeviceFilter() {
    this.showDeviceFilterDropdown = !this.showDeviceFilterDropdown;
    this.showStoreFilterDropdown = false;
  }

  toggleTestMachines() {
    this.showTestMachines = !this.showTestMachines;
    // Showing or hiding test machines changes the available stores/devices, so start again from "all selected".
    this.selectedStores.clear();
    this.selectedDevices.clear();
    this.calculateMonthlyStats(this.allMonthlyData);
    this.updateGridData(this.allDailyDataRaw);
  }

  getFilteredStores() {
    if (!this.storeFilterSearchText) return this.allUniqueStores;
    const lower = this.storeFilterSearchText.toLowerCase();
    return this.allUniqueStores.filter(s => s.toLowerCase().includes(lower));
  }

  getFilteredDevices() {
    if (!this.deviceFilterSearchText) return this.allUniqueDevices;
    const lower = this.deviceFilterSearchText.toLowerCase();
    return this.allUniqueDevices.filter(d => d.toLowerCase().includes(lower));
  }

  toggleAllStores() {
    if (this.isAllStoresSelected) {
      this.selectedStores.clear();
    } else {
      this.allUniqueStores.forEach(s => this.selectedStores.add(s));
    }
    this.applySort();
  }

  toggleAllDevices() {
    if (this.isAllDevicesSelected) {
      this.selectedDevices.clear();
    } else {
      this.allUniqueDevices.forEach(d => this.selectedDevices.add(d));
    }
    this.applySort();
  }

  toggleStoreSelection(store: string) {
    if (this.selectedStores.has(store)) {
      this.selectedStores.delete(store);
    } else {
      this.selectedStores.add(store);
    }
    this.applySort();
  }

  toggleDeviceSelection(device: string) {
    if (this.selectedDevices.has(device)) {
      this.selectedDevices.delete(device);
    } else {
      this.selectedDevices.add(device);
    }
    this.applySort();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-dropdown')) {
      this.showStoreFilterDropdown = false;
      this.showDeviceFilterDropdown = false;
    }
    if (!target.closest('.tooltip-wrapper')) {
      this.activeTooltipDeviceId = null;
    }
  }

  constructor() {
  }

  ngOnInit() {
    this.brandSub = this.brandService.selectedBrand$.subscribe(brand => {
      this.selectedBrand = brand;
      this.brandStoreNames.clear();
      // Reset store & device selections on brand change (AC-1)
      this.selectedStores.clear();
      this.selectedDevices.clear();
      this.fetchReport();
    });
  }

  ngOnDestroy() {
    this.brandSub?.unsubscribe();
  }

  onDateChange() {
    if (this.isDateRange && this.endDate < this.selectedDate) {
      this.endDate = new Date(this.selectedDate.getTime());
    }
    this.fetchReport();
  }

  private pingIntervalMs = 5 * 60 * 1000; // default 5 mins

  // Incremented per fetch so a slower, older fetch (e.g. the first page load) can never overwrite the newest one.
  private fetchSeq = 0;

  async fetchReport(forceRefresh = false) {
    const seq = ++this.fetchSeq;
    this.isLoading = true;
    
    const month = this.selectedDate.getMonth() + 1;
    const year = this.selectedDate.getFullYear();
    const period = `${year}-${String(month).padStart(2, '0')}`;
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(this.selectedDate.getDate()).padStart(2, '0')}`;

    const endMonth = this.endDate.getMonth() + 1;
    const endYear = this.endDate.getFullYear();
    const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(this.endDate.getDate()).padStart(2, '0')}`;

    try {
      // Resolve user scope if not loaded yet
      if (!this.userScope) {
        const user = this.auth.currentUser;
        if (user) {
          const profile = await firstValueFrom(this.accessService.getUserProfile(user.uid));
          this.userScope = await this.accessService.getUserPageScope('daily-report', profile);
          this.isSuperAdmin = !this.userScope.isRestricted && (profile?.role === 'admin' || profile?.role === 'superadmin' || profile?.role === 'developer');
        }
      }

      const pingIntervalMinutes = await this.photoboothService.getPingInterval().catch(() => 5);
      this.pingIntervalMs = pingIntervalMinutes * 60 * 1000;

      let monthlyData: any[] = [];
      let dailyData: any[] = [];

      if (this.isDateRange) {
        dailyData = await this.photoboothService.getAllDevicesDateRangeRevenue(dateStr, endDateStr);
        monthlyData = [];
        this.overriddenDatesThisMonth.clear();
      } else {
        const [mDat, dDat, overDates] = await Promise.all([
          this.photoboothService.getAllDevicesMonthlyRevenue(period),
          this.photoboothService.getAllDevicesDailyRevenue(period, dateStr),
          this.photoboothService.getPeriodOverriddenDays(period, this.selectedBrand?.id)
        ]);
        monthlyData = mDat;
        dailyData = dDat;
        this.overriddenDatesThisMonth = overDates || new Set<string>();
      }

      // A newer fetch (brand or date change) superseded this one: drop the stale result so the selection is initialised from the latest data.
      if (seq !== this.fetchSeq) return;

      // Count unassigned devices (AC-3)
      const allDevs = dailyData.length > 0 ? dailyData : monthlyData;
      this.unassignedDevicesCount = allDevs.filter(row => !row.deviceData?.brandId).length;

      // Filter by Brand / Company-Store Scope (KAN-314)
      let filteredMonthly = monthlyData;
      let filteredDaily = dailyData;

      if (this.selectedBrand?.id) {
        const brandStores = await this.getBrandStoreIds(this.selectedBrand.id);
        if (brandStores !== null) {
          this.brandStoreLookupFailed = false;
          // On successful company/store resolution, scope rows to eligible brand stores (an unbranded device at an eligible store remains in scope)
          const inBrand = (row: any) =>
            brandStores.has((row.deviceData?.storeId || '').toString().trim().toLowerCase());
          filteredMonthly = monthlyData.filter(inBrand);
          filteredDaily = dailyData.filter(inBrand);
        } else {
          this.brandStoreLookupFailed = true;
          // Fallback to device brandId filter only if company/store resolution fails (TC-07)
          filteredMonthly = monthlyData.filter(row => row.deviceData?.brandId === this.selectedBrand?.id);
          filteredDaily = dailyData.filter(row => row.deviceData?.brandId === this.selectedBrand?.id);
        }
      } else {
        this.brandStoreLookupFailed = false;
      }

      // Filter by Access Scope (AC-8)
      if (this.userScope && this.userScope.isRestricted) {
        const allowedStores = new Set(this.userScope.storeIds.map(s => s.toLowerCase()));
        filteredMonthly = filteredMonthly.filter(row => {
          const sid = (row.deviceData?.storeId || '').toString().toLowerCase();
          return allowedStores.has(sid);
        });
        filteredDaily = filteredDaily.filter(row => {
          const sid = (row.deviceData?.storeId || '').toString().toLowerCase();
          return allowedStores.has(sid);
        });
      }

      this.allMonthlyData = filteredMonthly;
      this.allDailyDataRaw = filteredDaily;
      if (!this.isDateRange) {
        this.calculateMonthlyStats(filteredMonthly);
      }
      this.updateGridData(filteredDaily);
      
    } catch (error) {
      console.error('Error fetching daily report:', error);
      alert('Failed to load report data.');
    } finally {
      if (seq === this.fetchSeq) {
        setTimeout(() => {
          this.zone.run(() => {
            this.isLoading = false;
          });
        }, 100);
      }
    }
  }

  brandStoreNames = new Map<string, string>();

  /**
   * Resolves lower-cased store IDs belonging to companies whose `brand` equals the given brand ID
   * (company.brand -> stores.companyIds / stores.companyId). Returns null if the lookup fails so the
   * caller falls back to the device-level brand filter only.
   */
  async getBrandStoreIds(brandId: string): Promise<Set<string> | null> {
    try {
      if (typeof window !== 'undefined' && (window as any)?.__MOCK_BRAND_STORE_LOOKUP_FAIL__) {
        throw new Error('Simulated company/store lookup failure');
      }
      this.brandStoreNames.clear();
      const [companiesRes, storesSnap] = await Promise.all([
        typeof this.companyService.getCompaniesSnapshot === 'function'
          ? this.companyService.getCompaniesSnapshot()
          : firstValueFrom(this.companyService.getCompanies()),
        this.storeService.getStoresSnapshot()
      ]);
      const brandCompanyIds = new Set<string>();
      if (companiesRes && typeof (companiesRes as any).forEach === 'function') {
        (companiesRes as any).forEach((d: any) => {
          const data = typeof d.data === 'function' ? d.data() : d;
          if ((data?.brand || '').toString().trim() === brandId) {
            brandCompanyIds.add(String(d.id).trim());
          }
        });
      } else if (Array.isArray(companiesRes)) {
        companiesRes
          .filter((c: any) => (c?.brand || '').toString().trim() === brandId)
          .forEach((c: any) => brandCompanyIds.add(String(c.id).trim()));
      }
      console.log('[KAN-314] getBrandStoreIds called for brandId:', brandId);
      console.log('[KAN-314] matched brandCompanyIds:', Array.from(brandCompanyIds));
      const result = new Set<string>();
      (storesSnap as any).forEach((d: any) => {
        const data = d.data();
        const cids: any[] = Array.isArray(data['companyIds']) && data['companyIds'].length > 0
          ? data['companyIds']
          : (data['companyId'] ? [data['companyId']] : []);
        if (cids.some(cid => brandCompanyIds.has(String(cid).trim()))) {
          const rawSid = data['storeId'] || d.id || '';
          const sid = rawSid.toString().trim().toLowerCase();
          const canonical = (data['storeId'] || data['name'] || d.id || '').toString().trim();
          if (sid) {
            result.add(sid);
            if (canonical && !this.brandStoreNames.has(sid)) {
              this.brandStoreNames.set(sid, canonical);
            }
          }
        }
      });
      console.log('[KAN-314] resolved brandStoreNames:', Array.from(this.brandStoreNames.values()));
      return result;
    } catch (e) {
      console.error('Error resolving brand stores for daily report:', e);
      return null;
    }
  }

  calculateMonthlyStats(monthlyData: any[]) {
    const validMonthlyData = this.showTestMachines ? monthlyData : monthlyData.filter(row => row.deviceData?.isTesting !== true);
    
    this.monthlyStats = validMonthlyData.reduce((stats, row) => {
      const data = row.data || {};
      const cardTotal = Number(data.CardTotal || data.cardTotal || data.credit || 0);
      const cashTotal = Number(data.CashTotal || data.cashTotal || data.cash || 0);
      const couponTotal = Number(data.CouponTotal || data.couponTotal || data.coupon || 0);
      const overCharge = Number(data.TotalOverpaidAmount || data.OverpaidAmount || data.overpaidAmount || data.overCharge || 0);
      
      stats.cash += cashTotal;
      stats.credit += cardTotal;
      stats.coupon += couponTotal;
      stats.overCharge += overCharge;
      stats.grandTotal += (cashTotal + cardTotal);
      return stats;
    }, { cash: 0, credit: 0, coupon: 0, overCharge: 0, grandTotal: 0 });
  }

  allRows: FilteredRow[] = [];
  allMonthlyData: any[] = [];

  updateGridData(dailyData: any[]) {
    const validDailyData = this.showTestMachines ? dailyData : dailyData.filter(row => row.deviceData?.isTesting !== true);

    const storeTotals = new Map<string, number>();

    const rows: FilteredRow[] = validDailyData.map(row => {
      const data = row.data || {};
      const deviceId = row.deviceId;
      
      let storeId = row.deviceData?.storeId || 'Unassigned';
      if (this.selectedBrand?.id && this.brandStoreNames.size > 0) {
        const lowerSid = (row.deviceData?.storeId || '').toString().trim().toLowerCase();
        if (this.brandStoreNames.has(lowerSid)) {
          storeId = this.brandStoreNames.get(lowerSid)!;
        }
      }
      const cardTotal = Number(data.CardTotal || data.cardTotal || data.credit || 0);
      const cashTotal = Number(data.CashTotal || data.cashTotal || data.cash || 0);
      const couponTotal = Number(data.CouponTotal || data.couponTotal || data.coupon || 0);
      const dailyOverpaid = Number(data.DailyOverpaidAmount || data.overpaidAmount || data.overCharge || 0);
      
      // Daily revenue and store revenue strictly exclude coupon amount (Cash + Card only)
      const dailyRev = cashTotal + cardTotal;
      
      storeTotals.set(storeId, (storeTotals.get(storeId) || 0) + dailyRev);

      let isOnline = false;
      const now = new Date().getTime();
      const dev = row.deviceData || {};
      const lastPingedDate = dev.lastSeen?.toDate ? dev.lastSeen.toDate() : dev.lastSeen;
      
      if (lastPingedDate) {
        const lastPingTime = new Date(lastPingedDate).getTime();
        isOnline = (now - lastPingTime) <= this.pingIntervalMs;
        if (isOnline && dev.deviceOnline === false) {
          isOnline = false;
        }
      } else if (dev.deviceOnline !== undefined) {
        isOnline = dev.deviceOnline;
      }

      const isDailyOverridden = data.isManualOverride === true || data.isOverridden === true;
      const monthlyMatch = this.allMonthlyData.find(m => m.deviceId === deviceId);
      const isMonthlyOverridden = monthlyMatch?.data?.isManualOverride === true;

      return {
        deviceId: deviceId,
        storeId: storeId,
        CardTotal: cardTotal,
        CashTotal: cashTotal,
        CouponTotal: couponTotal,
        DailyAnimefiedGenerated: Number(data.DailyAnimefiedGenerated || 0),
        DailyAnimefiedUsed: Number(data.DailyAnimefiedUsed || 0),
        DailyOverpaidAmount: dailyOverpaid,
        DailyRevenue: dailyRev,
        DailySessions: Number(data.DailySessions || data.dailySessions || data.totalSession || 0),
        Country: row.deviceData?.Country || data.Country || '',
        PrintsLeft: Number(row.deviceData?.printsLeft ?? row.deviceData?.remainingPapers ?? 0),
        StoreTotal: 0, // populated below
        deviceOnline: isOnline,
        cameraConnected: row.deviceData?.cameraConnected ?? false,
        cardMachineConnected: row.deviceData?.cardMachineConnected ?? false,
        billAcceptorConnected: row.deviceData?.billAcceptorConnected ?? false,
        isDailyOverridden: isDailyOverridden,
        isMonthlyOverridden: isMonthlyOverridden,
        overrideReason: data.overrideReason || '',
        updatedBy: data.updatedBy || '',
        updatedAt: data.updatedAt,
        originalValues: data.originalValues || null,
        overrideHistory: data.overrideHistory || []
      };
    });
    
    rows.forEach(r => r.StoreTotal = storeTotals.get(r.storeId) || 0);

    this.allRows = rows;
    this.calculateDailyStats(rows);
    
    // Populate dropdown options
    const devices = new Set<string>();
    rows.forEach(r => {
      devices.add(r.deviceId);
    });
    this.allUniqueDevices = Array.from(devices).sort();

    if (this.selectedBrand?.id && !this.brandStoreLookupFailed) {
      if (this.brandStoreNames.size > 0) {
        // Include all brand stores (including linked stores with 0 devices)
        const brandStoresList = Array.from(new Set(this.brandStoreNames.values()));
        if (this.userScope && this.userScope.isRestricted) {
          const allowed = new Set(this.userScope.storeIds.map(s => s.toLowerCase()));
          this.allUniqueStores = brandStoresList.filter(s => allowed.has(s.toLowerCase())).sort();
        } else {
          this.allUniqueStores = brandStoresList.sort();
        }
      } else {
        // Brand selected and lookup succeeded, but 0 matching stores found
        this.allUniqueStores = [];
      }
    } else {
      // No brand selected OR company/store lookup failed (fallback mode):
      // Populate unique stores from the visible device rows
      const stores = new Set<string>();
      rows.forEach(r => {
        if (r.storeId) stores.add(r.storeId);
      });
      this.allUniqueStores = Array.from(stores).sort();
    }

    // Init selection if this is the first load
    if (this.selectedStores.size === 0 && this.selectedDevices.size === 0) {
      this.allUniqueStores.forEach(s => this.selectedStores.add(s));
      this.allUniqueDevices.forEach(d => this.selectedDevices.add(d));
    }

    this.applySort();
  }

  filterList(list: FilteredRow[]): FilteredRow[] {
    return list.filter(row => {
      if (!this.selectedStores.has(row.storeId)) return false;
      if (!this.selectedDevices.has(row.deviceId)) return false;
      return true;
    });
  }

  calculateDailyStats(data: FilteredRow[]) {
    this.dailyStats = data.reduce((stats, row) => {
      stats.cash += row.CashTotal;
      stats.credit += row.CardTotal;
      stats.coupon += row.CouponTotal;
      stats.overCharge += row.DailyOverpaidAmount;
      stats.grandTotal += (row.CashTotal + row.CardTotal);
      return stats;
    }, { cash: 0, credit: 0, coupon: 0, overCharge: 0, grandTotal: 0 });
  }

  applyFilter() {
    this.zone.run(() => {
      this.filteredData = this.filterList(this.allRows);
      this.calculateDailyStats(this.filteredData);

      if (!this.isDateRange) {
        const filteredMonthly = this.allMonthlyData.filter(row => {
          const storeId = row.deviceData?.storeId || 'Unassigned';
          const deviceId = row.deviceId;
          if (!this.selectedStores.has(storeId)) return false;
          if (!this.selectedDevices.has(deviceId)) return false;
          return true;
        });
        this.calculateMonthlyStats(filteredMonthly);
      }
    });
  }

  private isSameDate(dateStr: string, dateObj: Date): boolean {
    // Assuming dateStr is YYYY-MM-DD or similar parsable string
    const d = new Date(dateStr);
    return d.getFullYear() === dateObj.getFullYear() &&
           d.getMonth() === dateObj.getMonth() &&
           d.getDate() === dateObj.getDate();
  }
}
